import React, { forwardRef } from 'react';
import { DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH } from 'components/common/DataGridDevEx/DataGrid.constants';
import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.css';
import DataGrid, { Column, Scrolling, GroupPanel, SearchPanel, LoadPanel, HeaderFilter, Editing, Button, RowDragging } from 'devextreme-react/data-grid';
import { IOutputFieldsGridProps, IQuickLinksGridsHandle } from './QuickLinksGrid.properties';
import { withStyles } from '@material-ui/core';
import styles from './QuickLinksGrid.styles';
import { ACTION_COLUMN_WIDTH, DRAG_COLUMN_WIDTH, FIELD_COLUMN_WIDTH } from '../QuickLinks.constants';
import { useFetchDashboardQuickLinks, useSetDashboardQuickLinksQueryData } from 'api/dashboardMaintenanceEnquiry/dashboardMaintenanceEnquiry';

const QuickLinksGrid = (props: IOutputFieldsGridProps, ref: React.Ref<IQuickLinksGridsHandle>) => {
    const { classes, setQuickLinksDataState, selectedDashboardId } = props;
    const params = new URLSearchParams(location.search);
    const dashboardId = selectedDashboardId || Number(params.get('DashboardId'));
    const { data: dashboardQuickLinksData, refetch: refetchDashboardQuickLinks } = useFetchDashboardQuickLinks(dashboardId);
    const setDashboardQuickLinksQueryData = useSetDashboardQuickLinksQueryData();
    const dataGridInnerRef = React.useRef<DataGrid>(null) as React.MutableRefObject<DataGrid | null>;

    React.useEffect(
        () => {
            setQuickLinksDataState(dashboardQuickLinksData);
        },
        [dashboardQuickLinksData]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            disableQuickLinksFunctionalities: dashboardQuickLinksData?.System,
            refetch: () => {
                refetchDashboardQuickLinks();
            },
            getData: () => {
                if (dataGridInnerRef.current) {

                    return dataGridInnerRef.current?.instance?.getDataSource()?.items();
                }

                return [];
            }
        }),
        [dashboardQuickLinksData, refetchDashboardQuickLinks]
    );

    const onDelete = React.useCallback(
        async (e: any): Promise<void> => {
            const rowKey = e?.row?.key;
            if (rowKey) {
                const { QuickLinkId } = rowKey;

                const filteredQuickLinks = dashboardQuickLinksData?.DashboardQuickLinks?.filter(
                    (item) => item.QuickLinkId !== QuickLinkId
                );

                const updatedQuickLinks = filteredQuickLinks.map((item, index) => ({
                    ...item,
                    Sequence: index + 1
                }));

                setDashboardQuickLinksQueryData(Number(dashboardId), {
                    DashboardQuickLinks: updatedQuickLinks
                });
            }
        },
        [dashboardId, dashboardQuickLinksData, setDashboardQuickLinksQueryData]
    );

    return (
        <DataGrid
            className={classes.QuickLinksGrid}
            dataSource={dashboardQuickLinksData?.DashboardQuickLinks || []}
            ref={dataGridInnerRef}
            showBorders={true}
            renderAsync={true}
            remoteOperations={true}
            allowColumnResizing={true}
            columnResizingMode={'nextColumn'}
            hoverStateEnabled={true}
            noDataText=''
            columnAutoWidth={true}
            disabled={false}
        >
            <Editing
                refreshMode={'reshape'}
                mode='row'
                allowAdding={false}
                allowDeleting={true}
                allowUpdating={true}
            />
            <Column
                type='drag'
                width={DRAG_COLUMN_WIDTH}
                fixed={true}
            />
            <HeaderFilter visible />
            <Column
                dataField={'Name'}
                caption={'Options'}
                allowHeaderFiltering={false}
                width={FIELD_COLUMN_WIDTH}
                allowSorting={false}
                allowEditing={false}
            >
            </Column>
            <Column
                dataField={'Label'}
                caption={'Labels'}
                allowHeaderFiltering={false}
                minWidth={FIELD_COLUMN_WIDTH}
                allowSorting={false}
            >
            </Column>
            <Column type={'buttons'} width={ACTION_COLUMN_WIDTH} allowResizing={false} fixed={true}>
                <Button name={'delete'} icon={'trash'} onClick={onDelete} />
            </Column>
            <RowDragging
                allowReordering={true}
                onReorder={async (e) => {
                    const visibleRows = e.component.getVisibleRows();
                    const reordered = [...dashboardQuickLinksData?.DashboardQuickLinks];

                    const fromIndex = reordered.indexOf(e.itemData);
                    const toIndex = reordered.indexOf(visibleRows[e.toIndex].data);

                    reordered.splice(fromIndex, 1);
                    reordered.splice(toIndex, 0, e.itemData);

                    const updatedQuickLinks = reordered.map((item, index) => ({
                        ...item,
                        Sequence: index + 1,
                    }));

                    setDashboardQuickLinksQueryData(Number(dashboardId), {
                        DashboardQuickLinks: updatedQuickLinks,
                    });
                }}
                showDragIcons={true}
            />
            <GroupPanel visible={false} />
            <SearchPanel visible={false} />
            <Scrolling mode={'infinite'} />
            <LoadPanel shading={false} height={DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH} width={DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH} text={''} showPane={false} />
        </DataGrid>
    );
};

export default withStyles(styles, { index: 1 })(forwardRef(QuickLinksGrid));
